import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { updateUser, updateRosterPlayer } from '../../lib/api';
import AccountEmailPanel from './AccountEmailPanel';
import { toast } from 'sonner';
import { getMemberUserId, getPlayerProfileId } from './memberIdentity';

// Fixed positions for players
const POSITIONS = ['Målvakt', 'Back', 'Mittfält', 'Anfall'];

// Predefined leader roles. `rawLabel` is the canonical Swedish string
// written back to `member.display_role` — this preserves the existing
// API payload / DB shape. The visible dropdown label is rendered at
// render-time via t('roles.<id>') so English users see 'Head coach' etc.
// while the DB continues to receive the exact same value as before.
const LEADER_ROLES = [
  { id: 'head_coach',       rawLabel: 'Huvudledare' },
  { id: 'assistant_coach',  rawLabel: 'Assisterande ledare' },
  { id: 'goalkeeper_coach', rawLabel: 'Målvaktstränare' },
  { id: 'fitness_coach',    rawLabel: 'Fystränare' },
  { id: 'analyst',          rawLabel: 'Analytiker' },
  { id: 'custom',           rawLabel: 'Egen roll...' }
];

// Known role IDs that receive the LEADER-shaped dialog.
const LEADER_SHAPE_ROLES = new Set([
  'coach', 'admin',
  'head_coach', 'assistant_coach',
  'team_admin', 'team_manager',
  'org_admin', 'app_admin',
  'goalkeeper_coach', 'fitness_coach', 'analyst',
  'leader', 'staff',
]);

// Map display role to role id (reverse lookup — matches the canonical
// Swedish rawLabel that was written into `member.display_role`).
const getRoleIdFromDisplayRole = (displayRole) => {
  const found = LEADER_ROLES.find(r => r.rawLabel === displayRole);
  return found ? found.id : 'custom';
};

const splitPlayerName = (value) => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' '),
  };
};

export default function MemberEditDialog({
  member,
  open,
  onClose,
  onSaved,
  teamId
}) {
  const { t } = useTranslation('team_admin');
  const [saving, setSaving] = useState(false);
  const [emailDirty, setEmailDirty] = useState(false);
  const [form, setForm] = useState({
    name: '',
    position: '',
    jersey_number: '',
    role_type: '',
    custom_role: ''
  });

  // In the hard-cut Trupp model, a player row is a player_profile. Leaders are
  // authentication actors/memberships and continue to edit the user account.
  const isPlayer = !!member && (member.role === 'player' || (!member.role && !!getPlayerProfileId(member)));
  const isCoach = !!member && !isPlayer && LEADER_SHAPE_ROLES.has(member.role);

  // Initialize form when member changes
  useEffect(() => {
    if (member) {
      const displayRole = member.display_role || '';
      const isLeaderShape = LEADER_SHAPE_ROLES.has(member.role);
      const roleId = (!displayRole && isLeaderShape)
        ? 'head_coach'
        : getRoleIdFromDisplayRole(displayRole);

      setForm({
        name: member.name || member.display_name || [member.first_name, member.last_name].filter(Boolean).join(' '),
        position: member.position || '',
        jersey_number: member.jersey_number?.toString() || '',
        role_type: roleId,
        custom_role: roleId === 'custom' ? displayRole : ''
      });
    }
  }, [member]);

  const handleSave = async () => {
    if (!member || emailDirty) return;

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      toast.error(t('member_name_required', { defaultValue: 'Namn krävs' }));
      return;
    }

    setSaving(true);
    try {
      if (isPlayer) {
        const playerProfileId = getPlayerProfileId(member);
        if (!playerProfileId) {
          throw new Error('Canonical player_profile_id is missing');
        }

        const jersey = form.jersey_number ? parseInt(form.jersey_number, 10) : null;
        const currentProfileName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
        const canonicalName = currentProfileName && trimmedName === currentProfileName
          ? {
              first_name: member.first_name || '',
              last_name: member.last_name || '',
            }
          : splitPlayerName(trimmedName);

        // PLAYER_PROFILE is the football identity and the source of truth for
        // Trupp. Do not write team-scoped player data back to the auth user.
        await updateRosterPlayer(playerProfileId, {
          ...canonicalName,
          jersey_number: jersey,
          position: form.position || null,
        });
      } else if (isCoach) {
        const updates = { name: trimmedName };

        // Leaders remain user/membership identities. display_role is a user-facing
        // label only; membership role/permissions are not changed here.
        if (form.role_type === 'custom') {
          updates.display_role = form.custom_role || 'Ledare';
        } else {
          const roleRaw = LEADER_ROLES.find(r => r.id === form.role_type)?.rawLabel || 'Ledare';
          updates.display_role = roleRaw;
        }

        const userId = getMemberUserId(member);
        if (!userId) throw new Error('Canonical user_id is missing');
        await updateUser(userId, updates);
      } else {
        throw new Error(`Unsupported roster member role: ${member.role || 'unknown'}`);
      }

      toast.success(t('member_updated_toast'));
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(t('member_update_failed_toast'));
    } finally {
      setSaving(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle>{isCoach ? t('member_edit_leader_title') : t('member_edit_player_title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('member_field_name')}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-background"
              data-testid="edit-name-input"
            />
          </div>

          {isPlayer && open && getPlayerProfileId(member) && teamId && (
            <AccountEmailPanel key={`${teamId}:${getPlayerProfileId(member)}`} teamId={teamId}
              profileId={getPlayerProfileId(member)} onChanged={onSaved} onDirtyChange={setEmailDirty} />
          )}

          {isPlayer && (
            <>
              <div className="space-y-2">
                <Label>{t('member_field_position')}</Label>
                <Select
                  value={form.position}
                  onValueChange={(v) => setForm({ ...form, position: v })}
                >
                  <SelectTrigger data-testid="edit-position-select">
                    <SelectValue placeholder={t('member_placeholder_position')} />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map(pos => (
                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('member_field_jersey')}</Label>
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={form.jersey_number}
                  onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                  className="bg-background"
                  data-testid="edit-jersey-input"
                />
              </div>
            </>
          )}

          {isCoach && (
            <>
              <div className="space-y-2">
                <Label>{t('member_field_role')}</Label>
                <Select
                  value={form.role_type}
                  onValueChange={(v) => setForm({
                    ...form,
                    role_type: v,
                    custom_role: v === 'custom' ? form.custom_role : ''
                  })}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue placeholder={t('member_placeholder_role')} />
                  </SelectTrigger>
                  <SelectContent>
                    {LEADER_ROLES.map(role => (
                      <SelectItem key={role.id} value={role.id}>{t(`roles.${role.id}`, role.rawLabel)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.role_type === 'custom' && (
                <div className="space-y-2">
                  <Label>{t('member_field_custom_role')}</Label>
                  <Input
                    value={form.custom_role}
                    onChange={(e) => setForm({ ...form, custom_role: e.target.value })}
                    placeholder={t('member_placeholder_custom_role')}
                    className="bg-background"
                    data-testid="edit-custom-role-input"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {emailDirty && <p className="text-xs text-muted-foreground">Slutför eller avbryt e-poständringen ovan innan du sparar övriga uppgifter.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || emailDirty} data-testid="save-member-btn">
            {saving ? t('member_saving') : t('member_save_changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
